import { useRef } from "react"
import type { ChangeEvent, DragEvent, MouseEvent } from "react"

/**
 * Wire a hidden file input and a drop target so callers can accept one file or many.
 */
export const useFileDropzone = (options: {
    accept: string
    onFilesSelected: (files: File[]) => void | Promise<void>
    multiple?: boolean
    disabled?: boolean
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null)

    const onSelectFiles = (files: FileList | null) => {
        const selectedFiles = Array.from(files ?? [])

        if (options.disabled || !selectedFiles.length) {
            return
        }

        void options.onFilesSelected(selectedFiles)
    }

    const onDragOver = (event: DragEvent) => {
        event.preventDefault()
        event.stopPropagation()
    }

    const onDrop = (event: DragEvent) => {
        onDragOver(event)

        onSelectFiles(event.dataTransfer?.files ?? null)
    }

    const onClick = (event?: MouseEvent) => {
        event?.stopPropagation()

        if (!options.disabled) {
            fileInputRef.current?.click()
        }
    }

    const onChange = (event: ChangeEvent<HTMLInputElement>) => {
        onSelectFiles(event.target.files)

        // Cleared so picking the same file again still fires a change event
        event.target.value = ""
    }

    return {
        fileInputProps: {
            ref: fileInputRef,
            type: "file" as const,
            accept: options.accept,
            multiple: options.multiple,
            disabled: options.disabled,
            className: "hidden",
            onChange,
        },
        dropzoneProps: {
            onDrop,
            onDragOver,
            onClick,
        },
    }
}
